<template>
  <div class="p-4 md:p-6 lg:p-8 bg-app-bg-light dark:bg-app-bg-dark min-h-screen">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6">
      <button
        @click="$router.push('/inicio')"
        class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-custom-primary dark:hover:text-custom-primary transition-colors border-0 bg-transparent"
      >
        <i class="bx bx-arrow-back text-lg"></i>
        Regresar
      </button>
      <div class="h-5 w-px bg-slate-300 dark:bg-slate-600"></div>
      <div>
        <h1 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100">
          Liquidación de Tarjetas de Crédito
        </h1>
        <p class="text-xs text-slate-500 dark:text-slate-400">BAC Credomatic · Visanet — Comisiones y retenciones bancarias</p>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6">
      <div class="flex flex-wrap gap-3 items-end">
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Mes</label>
          <select v-model="filtro.mes" class="form-control form-control-sm">
            <option v-for="m in meses" :key="m.val" :value="m.val">{{ m.label }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Año</label>
          <select v-model="filtro.anio" class="form-control form-control-sm">
            <option v-for="a in anios" :key="a" :value="a">{{ a }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Banco</label>
          <select v-model="filtro.banco" class="form-control form-control-sm">
            <option value="">Todos</option>
            <option value="BAC_CREDOMATIC">BAC Credomatic</option>
            <option value="VISANET">Visanet</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Desde</label>
          <input type="date" v-model="filtro.fecha_inicio" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hasta</label>
          <input type="date" v-model="filtro.fecha_fin" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hora inicio</label>
          <input type="time" v-model="filtro.hora_inicio" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hora fin</label>
          <input type="time" v-model="filtro.hora_fin" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Ordenar por</label>
          <select v-model="ordenar" class="form-control form-control-sm">
            <option value="fecha_desc">Fecha (más reciente)</option>
            <option value="fecha_asc">Fecha (más antigua)</option>
            <option value="liquido_desc">Líquido (mayor)</option>
            <option value="liquido_asc">Líquido (menor)</option>
          </select>
        </div>
        <button @click="cargar" class="btn btn-outline-secondary btn-sm">
          <i class="bx bx-search mr-1"></i> Filtrar
        </button>
        <button @click="exportarPDF" class="btn btn-outline-primary btn-sm">
          <i class="bx bx-file-pdf mr-1"></i> PDF
        </button>
        <button @click="exportarExcel" class="btn btn-outline-success btn-sm">
          <i class="bx bx-spreadsheet mr-1"></i> Excel
        </button>
      </div>
    </div>

    <!-- Resumen por banco -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" v-if="Object.keys(porBanco).length > 0">
      <div
        v-for="(t, banco) in porBanco"
        :key="banco"
        class="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4"
        :class="banco === 'BAC_CREDOMATIC' ? 'border-red-500' : 'border-blue-500'"
      >
        <div class="flex items-center gap-3 mb-3">
          <div class="p-2 rounded-lg" :class="banco === 'BAC_CREDOMATIC' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-blue-100 dark:bg-blue-900/30'">
            <i class="bx bx-credit-card text-2xl" :class="banco === 'BAC_CREDOMATIC' ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'"></i>
          </div>
          <h3 class="font-bold text-slate-800 dark:text-slate-100">
            {{ banco === 'BAC_CREDOMATIC' ? 'BAC Credomatic' : 'Visanet' }}
          </h3>
        </div>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-slate-500 dark:text-slate-400">Hospedaje:</span>
            <span class="font-mono font-semibold text-slate-700 dark:text-slate-300">Q{{ fmt(t.hospedaje_recargo) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-amber-600 dark:text-amber-400">Restaurante:</span>
            <span class="font-mono font-semibold text-amber-700 dark:text-amber-300">Q{{ fmt(t.restaurante_alimentacion) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-slate-500 dark:text-slate-400">Base comisión:</span>
            <span class="font-mono font-semibold text-slate-700 dark:text-slate-300">Q{{ fmt(t.base_comision) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-slate-500 dark:text-slate-400">Comisión (3%):</span>
            <span class="font-mono text-red-500">Q{{ fmt(t.comision) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-slate-500 dark:text-slate-400">IVA:</span>
            <span class="font-mono text-yellow-600 dark:text-yellow-400">Q{{ fmt(t.iva) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-slate-500 dark:text-slate-400">Ret. IVA:</span>
            <span class="font-mono text-orange-500">Q{{ fmt(t.retencion_iva) }}</span>
          </div>
          <div class="flex justify-between col-span-2 pt-1 border-t border-slate-100 dark:border-slate-700">
            <span class="text-slate-600 dark:text-slate-400 font-medium">Líquido:</span>
            <span class="font-mono font-bold text-custom-primary">Q{{ fmt(t.liquido) }}</span>
          </div>
          <div class="flex justify-between col-span-2">
            <span class="text-slate-600 dark:text-slate-400 font-medium">Transferido:</span>
            <span class="font-mono font-semibold" :class="t.transferido > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'">
              {{ t.transferido > 0 ? 'Q' + fmt(t.transferido) : 'Pendiente' }}
            </span>
          </div>
          <div class="flex justify-between col-span-2">
            <span class="text-slate-600 dark:text-slate-400 font-medium">Diferencia:</span>
            <span class="font-mono font-bold" :class="Math.abs(t.diferencia) < 0.01 ? 'text-green-600 dark:text-green-400' : 'text-red-500'">
              Q{{ fmt(t.diferencia) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla principal -->
    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden mb-6">
      <div class="overflow-x-auto">
        <table class="w-full text-xs md:text-sm">
          <thead>
            <tr class="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Fecha</th>
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Banco</th>
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Tipo</th>
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">No. Facturas</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                Hospedaje <span class="text-custom-primary font-normal">(A)</span>
              </th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                Restaurante <span class="text-amber-500 font-normal">(B)</span>
              </th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                Otros <span class="text-teal-500 font-normal">(C)</span>
              </th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Base<br>Comisión</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">IVA</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Comisión</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Ret.IVA</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Admin<br>Fee</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Líquido</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Transferido</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Diferencia</th>
              <th class="px-3 py-3 text-center font-semibold text-slate-700 dark:text-slate-300">Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="16" class="text-center py-10">
                <i class="bx bx-loader-alt bx-spin text-3xl text-custom-primary"></i>
              </td>
            </tr>
            <tr v-else-if="registros.length === 0">
              <td colspan="16" class="text-center py-10 text-slate-500 dark:text-slate-400">
                No hay pagos con tarjeta para el período seleccionado.
              </td>
            </tr>
            <tr
              v-for="r in registrosOrdenados"
              :key="r.fecha + '_' + r.banco"
              class="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
              @click="abrirModal(r)"
            >
              <td class="px-3 py-2.5 text-slate-700 dark:text-slate-300 whitespace-nowrap font-medium">{{ formatFecha(r.fecha) }}</td>
              <td class="px-3 py-2.5 whitespace-nowrap">
                <span class="px-2 py-0.5 rounded-full text-xs font-semibold"
                  :class="r.banco === 'BAC_CREDOMATIC'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'">
                  {{ r.banco === 'BAC_CREDOMATIC' ? 'BAC' : 'VISA' }}
                </span>
              </td>
              <td class="px-3 py-2.5 text-slate-500 dark:text-slate-400">{{ r.tipo_tarjeta || '—' }}</td>
              <td class="px-3 py-2.5 text-slate-500 dark:text-slate-400 max-w-[120px] truncate" :title="r.facturas_emitidas">{{ r.facturas_emitidas || '—' }}</td>
              <td class="px-3 py-2.5 text-right font-mono text-slate-700 dark:text-slate-300">{{ fmt(r.hospedaje_recargo) }}</td>
              <td class="px-3 py-2.5 text-right font-mono text-amber-600 dark:text-amber-400">{{ fmt(r.restaurante_alimentacion) }}</td>
              <td class="px-3 py-2.5 text-right font-mono text-teal-600 dark:text-teal-400">{{ fmt(r.otros) }}</td>
              <td class="px-3 py-2.5 text-right font-mono font-semibold text-slate-800 dark:text-slate-100">{{ fmt(r.base_comision) }}</td>
              <td class="px-3 py-2.5 text-right font-mono text-yellow-600 dark:text-yellow-400">{{ fmt(r.iva) }}</td>
              <td class="px-3 py-2.5 text-right font-mono text-red-500">{{ fmt(r.comision) }}</td>
              <td class="px-3 py-2.5 text-right font-mono text-orange-500">{{ fmt(r.retencion_iva) }}</td>
              <td class="px-3 py-2.5 text-right font-mono text-slate-500">{{ r.admin_fee > 0 ? fmt(r.admin_fee) : '—' }}</td>
              <td class="px-3 py-2.5 text-right font-mono font-bold text-custom-primary">{{ fmt(r.liquido) }}</td>
              <td class="px-3 py-2.5 text-right font-mono"
                :class="r.transferido > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'">
                {{ r.transferido > 0 ? fmt(r.transferido) : '—' }}
              </td>
              <td class="px-3 py-2.5 text-right font-mono font-semibold"
                :class="r.transferido == 0
                  ? 'text-slate-400'
                  : Math.abs(r.diferencia) < 0.01
                    ? 'text-green-600 dark:text-green-400'
                    : r.diferencia > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-500'">
                {{ r.transferido > 0 ? fmt(r.diferencia) : '—' }}
              </td>
              <td class="px-3 py-2.5 text-center" @click.stop>
                <span v-if="r.guardado" @click="abrirModal(r)"
                  class="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium cursor-pointer hover:underline">
                  <i class="bx bx-check-circle"></i> Guardado
                </span>
                <span v-else @click="abrirModal(r)"
                  class="inline-flex items-center gap-1 text-xs text-amber-500 font-medium cursor-pointer hover:underline">
                  <i class="bx bx-pencil"></i> Pendiente
                </span>
                <button v-if="r.guardado" @click.stop="eliminar(r.id)" class="ml-2 text-red-400 hover:text-red-600 border-0 bg-transparent p-0">
                  <i class="bx bx-trash text-lg"></i>
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="registros.length > 0">
            <tr class="bg-slate-100 dark:bg-slate-900 font-bold text-slate-800 dark:text-slate-100 border-t-2 border-slate-300 dark:border-slate-600 text-xs">
              <td colspan="4" class="px-3 py-3">TOTALES</td>
              <td class="px-3 py-3 text-right font-mono">{{ fmt(totales.hospedaje_recargo) }}</td>
              <td class="px-3 py-3 text-right font-mono text-amber-600 dark:text-amber-400">{{ fmt(totales.restaurante_alimentacion) }}</td>
              <td class="px-3 py-3 text-right font-mono text-teal-600 dark:text-teal-400">{{ fmt(totales.otros) }}</td>
              <td class="px-3 py-3 text-right font-mono">{{ fmt(totales.base_comision) }}</td>
              <td class="px-3 py-3 text-right font-mono text-yellow-600 dark:text-yellow-400">{{ fmt(totales.iva) }}</td>
              <td class="px-3 py-3 text-right font-mono text-red-500">{{ fmt(totales.comision) }}</td>
              <td class="px-3 py-3 text-right font-mono text-orange-500">{{ fmt(totales.retencion_iva) }}</td>
              <td class="px-3 py-3 text-right font-mono text-slate-500">{{ fmt(totales.admin_fee) }}</td>
              <td class="px-3 py-3 text-right font-mono text-custom-primary">{{ fmt(totales.liquido) }}</td>
              <td class="px-3 py-3 text-right font-mono text-green-600 dark:text-green-400">{{ fmt(totales.transferido) }}</td>
              <td class="px-3 py-3 text-right font-mono"
                :class="Math.abs(totales.diferencia) < 0.01 ? 'text-green-600' : 'text-red-500'">
                {{ fmt(totales.diferencia) }}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- ── Modal Registrar / Editar Liquidación ───────────────────────────────── -->
    <div v-if="modal.visible" class="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 p-4">
      <div class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div class="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100">
              {{ modal.guardado ? 'Editar liquidación' : 'Registrar liquidación' }}
              — {{ formatFecha(modal.row.fecha) }}
              <span class="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold"
                :class="modal.row.banco === 'BAC_CREDOMATIC'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'">
                {{ modal.row.banco === 'BAC_CREDOMATIC' ? 'BAC Credomatic' : 'Visanet' }}
              </span>
            </h2>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Los montos se calculan automáticamente desde los pagos con tarjeta del sistema
            </p>
          </div>
          <button @click="cerrarModal" class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent text-2xl leading-none">&times;</button>
        </div>

        <!-- Resumen del día (solo lectura) -->
        <div class="p-5 border-b border-slate-200 dark:border-slate-700">
          <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Montos cobrados con tarjeta (calculado del sistema)
          </p>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <div class="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
              <p class="text-xs text-slate-500 dark:text-slate-400">Hospedaje (A)</p>
              <p class="text-base font-bold text-slate-800 dark:text-slate-100">Q{{ fmt(modal.row.hospedaje_recargo) }}</p>
            </div>
            <div class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
              <p class="text-xs text-amber-600 dark:text-amber-400">Restaurante (B)</p>
              <p class="text-base font-bold text-amber-700 dark:text-amber-300">Q{{ fmt(modal.row.restaurante_alimentacion) }}</p>
            </div>
            <div class="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3">
              <p class="text-xs text-teal-600 dark:text-teal-400">Otros (C)</p>
              <p class="text-base font-bold text-teal-700 dark:text-teal-300">Q{{ fmt(modal.row.otros) }}</p>
            </div>
          </div>
          <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Descuentos del banco (calculado)
          </p>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
              <p class="text-xs text-slate-500 dark:text-slate-400">Base Comisión</p>
              <p class="font-bold text-slate-800 dark:text-slate-100 font-mono">Q{{ fmt(modal.row.base_comision) }}</p>
            </div>
            <div class="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
              <p class="text-xs text-yellow-600 dark:text-yellow-400">IVA 12%</p>
              <p class="font-bold text-yellow-700 dark:text-yellow-400 font-mono">Q{{ fmt(modal.row.iva) }}</p>
            </div>
            <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
              <p class="text-xs text-red-500">Comisión 3%</p>
              <p class="font-bold text-red-600 dark:text-red-400 font-mono">Q{{ fmt(modal.row.comision) }}</p>
            </div>
            <div class="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
              <p class="text-xs text-orange-500">Ret. IVA 15%</p>
              <p class="font-bold text-orange-600 dark:text-orange-400 font-mono">Q{{ fmt(modal.row.retencion_iva) }}</p>
            </div>
          </div>
          <div class="mt-3 bg-custom-primary/10 dark:bg-custom-primary/20 rounded-lg p-3 flex items-center justify-between">
            <p class="text-sm text-custom-primary font-medium">Líquido a recibir del banco</p>
            <p class="text-xl font-bold text-custom-primary font-mono">Q{{ fmt(calcLiquido) }}</p>
          </div>
          <div v-if="modal.row.facturas_emitidas" class="mt-2 text-xs text-slate-500 dark:text-slate-400">
            <i class="bx bx-receipt mr-1"></i>Facturas FEL detectadas: {{ modal.row.facturas_emitidas }}
          </div>
        </div>

        <form @submit.prevent="guardar" class="p-5">
          <p class="text-xs font-semibold text-custom-primary uppercase tracking-wider mb-3 border-b border-slate-200 dark:border-slate-700 pb-1">
            Datos de la liquidación
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Tipo de Tarjeta</label>
              <input type="text" v-model="form.tipo_tarjeta" class="form-control" placeholder="VISA, MASTERCARD, TODAS..." />
            </div>
            <div>
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Admin. Fee
                <span class="text-slate-400 font-normal ml-1">— solo Visanet</span>
              </label>
              <input type="number" v-model.number="form.admin_fee" step="0.01" min="0" class="form-control" placeholder="0.00" />
            </div>
            <div class="md:col-span-2">
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Facturas Emitidas No.</label>
              <textarea v-model="form.facturas_emitidas" rows="2" class="form-control" placeholder="Se detecta automáticamente desde FEL. Completa si hay facturas adicionales."></textarea>
            </div>

            <!-- Transferido -->
            <div class="md:col-span-2">
              <p class="text-xs font-semibold text-custom-primary uppercase tracking-wider mb-3 border-b border-slate-200 dark:border-slate-700 pb-1">
                Monto recibido del banco
                <span class="text-xs font-normal text-slate-400 ml-2">— completar al recibir el estado de cuenta</span>
              </p>
            </div>
            <div>
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Monto Transferido</label>
              <input type="number" v-model.number="form.transferido" step="0.01" min="0" class="form-control" placeholder="0.00" />
              <p class="text-xs text-slate-400 mt-1">Dejar en 0 si aún no se ha recibido</p>
            </div>
            <div class="rounded-lg p-3 flex flex-col justify-center"
              :class="form.transferido == 0
                ? 'bg-slate-50 dark:bg-slate-700/50'
                : Math.abs(calcDiferencia) < 0.01
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'bg-red-50 dark:bg-red-900/20'">
              <p class="text-xs font-medium"
                :class="form.transferido == 0 ? 'text-slate-500' : Math.abs(calcDiferencia) < 0.01 ? 'text-green-600 dark:text-green-400' : 'text-red-500'">
                {{ form.transferido == 0 ? 'Diferencia (al recibir)' : 'Diferencia (Líquido − Transferido)' }}
              </p>
              <p class="text-xl font-bold font-mono"
                :class="form.transferido == 0 ? 'text-slate-400' : Math.abs(calcDiferencia) < 0.01 ? 'text-green-600 dark:text-green-400' : 'text-red-500'">
                {{ form.transferido == 0 ? '—' : 'Q' + fmt(calcDiferencia) }}
              </p>
            </div>

            <div class="md:col-span-2">
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Observaciones</label>
              <textarea v-model="form.observaciones" rows="2" class="form-control" placeholder="Notas del estado de cuenta, diferencias, etc."></textarea>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button type="button" @click="cerrarModal" class="btn btn-outline-secondary">Cancelar</button>
            <button type="submit" :disabled="guardando" class="btn text-white" style="background:#B39659; border-color:#B39659;">
              <i class="bx bx-loader-alt bx-spin mr-1" v-if="guardando"></i>
              {{ guardando ? 'Guardando...' : (modal.guardado ? 'Actualizar' : 'Guardar Registro') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '../../config/config.js';
import { BRAND } from '@/config/branding';

const formVacio = () => ({
  tipo_tarjeta:      '',
  facturas_emitidas: '',
  transferido:       0,
  admin_fee:         0,
  observaciones:     '',
});

export default {
  name: 'LiquidacionTarjetas',
  data() {
    const hoy = new Date();
    return {
      loading:   false,
      guardando: false,
      registros: [],
      totales:   {},
      porBanco:  {},
      filtro: {
        mes:          hoy.getMonth() + 1,
        anio:         hoy.getFullYear(),
        banco:        '',
        fecha_inicio: '',
        fecha_fin:    '',
        hora_inicio:  '00:00',
        hora_fin:     '23:59',
      },
      ordenar: 'fecha_desc',
      modal: { visible: false, guardado: false, id: null, row: {} },
      form: formVacio(),
      meses: [
        { val: 1, label: 'Enero' }, { val: 2, label: 'Febrero' }, { val: 3, label: 'Marzo' },
        { val: 4, label: 'Abril' }, { val: 5, label: 'Mayo' }, { val: 6, label: 'Junio' },
        { val: 7, label: 'Julio' }, { val: 8, label: 'Agosto' }, { val: 9, label: 'Septiembre' },
        { val: 10, label: 'Octubre' }, { val: 11, label: 'Noviembre' }, { val: 12, label: 'Diciembre' },
      ],
      anios: Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i),
    };
  },
  computed: {
    registrosOrdenados() {
      const arr = [...this.registros];
      switch (this.ordenar) {
        case 'fecha_asc':    return arr.sort((a, b) => a.fecha.localeCompare(b.fecha));
        case 'liquido_desc': return arr.sort((a, b) => b.liquido - a.liquido);
        case 'liquido_asc':  return arr.sort((a, b) => a.liquido - b.liquido);
        default:             return arr.sort((a, b) => b.fecha.localeCompare(a.fecha));
      }
    },
    // Live liquido based on row amounts + form admin_fee
    calcLiquido() {
      const row = this.modal.row || {};
      const base    = (row.base_comision || 0);
      const com     = (row.comision      || 0);
      const ivacom  = (row.iva_comision  || 0);
      const ret     = (row.retencion_iva || 0);
      const adm     = this.form.admin_fee || 0;
      return parseFloat((base - com - ivacom - ret - adm).toFixed(2));
    },
    calcDiferencia() {
      return parseFloat((this.calcLiquido - (this.form.transferido || 0)).toFixed(2));
    },
  },
  methods: {
    fmt(num) {
      return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num ?? 0);
    },
    formatFecha(f) {
      if (!f) return '—';
      const d = new Date(f.substring(0, 10) + 'T12:00:00');
      return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    async cargar() {
      try {
        this.loading = true;
        const params = {};
        if (this.filtro.fecha_inicio) {
          params.fecha_inicio = this.filtro.fecha_inicio;
          params.fecha_fin    = this.filtro.fecha_fin;
        } else {
          params.mes  = this.filtro.mes;
          params.anio = this.filtro.anio;
        }
        params.hora_inicio = this.filtro.hora_inicio;
        params.hora_fin    = this.filtro.hora_fin;
        if (this.filtro.banco) params.banco = this.filtro.banco;
        const { data } = await axios.get(`${API_URL}/contabilidad/liquidaciones-tarjetas`, { params });
        this.registros = data.registros;
        this.totales   = data.totales;
        this.porBanco  = data.por_banco;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    // ── Modal ──────────────────────────────────────────────────────────────────
    abrirModal(r) {
      this.modal = {
        visible:  true,
        guardado: r.guardado,
        id:       r.id,
        row:      r,
      };
      this.form = {
        tipo_tarjeta:      r.tipo_tarjeta      || '',
        facturas_emitidas: r.facturas_emitidas || '',
        transferido:       r.transferido       || 0,
        admin_fee:         r.admin_fee         || 0,
        observaciones:     r.observaciones     || '',
      };
    },
    cerrarModal() {
      this.modal = { visible: false, guardado: false, id: null, row: {} };
      this.form  = formVacio();
    },
    async guardar() {
      try {
        this.guardando = true;
        const payload = {
          ...this.form,
          banco: this.modal.row.banco,
          fecha: this.modal.row.fecha,
        };
        if (this.modal.guardado) {
          await axios.put(`${API_URL}/contabilidad/liquidaciones-tarjetas/${this.modal.id}`, payload);
        } else {
          await axios.post(`${API_URL}/contabilidad/liquidaciones-tarjetas`, payload);
        }
        this.cerrarModal();
        await this.cargar();
      } catch (e) {
        console.error(e);
        alert('Error al guardar. Verifique los datos.');
      } finally {
        this.guardando = false;
      }
    },
    async eliminar(id) {
      if (!confirm('¿Eliminar este registro de liquidación?')) return;
      try {
        await axios.delete(`${API_URL}/contabilidad/liquidaciones-tarjetas/${id}`);
        await this.cargar();
      } catch (e) {
        console.error(e);
      }
    },

    // ── PDF ───────────────────────────────────────────────────────────────────
    async exportarPDF() {
      try {
        const params = {};
        if (this.filtro.fecha_inicio) {
          params.fecha_inicio = this.filtro.fecha_inicio;
          params.fecha_fin    = this.filtro.fecha_fin;
        } else {
          params.mes  = this.filtro.mes;
          params.anio = this.filtro.anio;
        }
        params.hora_inicio = this.filtro.hora_inicio;
        params.hora_fin    = this.filtro.hora_fin;
        params.ordenar     = this.ordenar;
        if (this.filtro.banco) params.banco = this.filtro.banco;
        const response = await axios.get(`${API_URL}/contabilidad/liquidaciones-tarjetas/pdf`, {
          params,
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `liquidacion-tarjetas-${this.filtro.mes || this.filtro.fecha_inicio}-${this.filtro.anio || this.filtro.fecha_fin}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (e) {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          try {
            const json = JSON.parse(text);
            alert(json.error || json.message || 'Error al generar el PDF.');
          } catch { alert('Error al generar el PDF.'); }
        } else {
          console.error(e);
          alert('Error al generar el PDF.');
        }
      }
    },

    // ── Excel ─────────────────────────────────────────────────────────────────
    exportarExcel() {
      var BOM = '\uFEFF';
      var numFmt = function(n) { return (parseFloat(n) || 0).toFixed(2); };
      var esc = function(v) {
        var s = String(v ?? '');
        if (s.indexOf(';') !== -1 || s.indexOf('"') !== -1 || s.indexOf('\n') !== -1) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      };
      var rowToLine = function(arr) { return arr.map(esc).join(';'); };
      var t = this.totales || {};
      var mesMes = this.meses.find(function(m) { return m.val === this.filtro.mes; }, this);
      var mesLabel = mesMes ? mesMes.label : '';
      var lines = [
        'LIQUIDACION DE TARJETAS DE CREDITO - ' + BRAND.nombre.toUpperCase(),
        'Periodo: ' + mesLabel + ' ' + this.filtro.anio,
        '',
        rowToLine([
          'Fecha', 'Banco', 'Tipo Tarjeta', 'No. Facturas',
          'Hospedaje (A)', 'Restaurante (B)', 'Otros (C)',
          'Base Comision', 'IVA', 'Comision 3%', 'Ret.IVA 15%', 'Admin Fee',
          'Liquido', 'Transferido', 'Diferencia', 'Observaciones',
        ]),
      ];
      this.registros.forEach(function(r) {
        lines.push(rowToLine([
          (r.fecha || '').substring(0, 10),
          r.banco === 'BAC_CREDOMATIC' ? 'BAC Credomatic' : 'Visanet',
          r.tipo_tarjeta || '',
          r.facturas_emitidas || '',
          numFmt(r.hospedaje_recargo),
          numFmt(r.restaurante_alimentacion),
          numFmt(r.otros),
          numFmt(r.base_comision),
          numFmt(r.iva),
          numFmt(r.comision),
          numFmt(r.retencion_iva),
          numFmt(r.admin_fee),
          numFmt(r.liquido),
          numFmt(r.transferido),
          numFmt(r.diferencia),
          r.observaciones || '',
        ]));
      });
      lines.push(rowToLine([
        'TOTALES', '', '', '',
        numFmt(t.hospedaje_recargo), numFmt(t.restaurante_alimentacion), numFmt(t.otros),
        numFmt(t.base_comision),
        numFmt(t.iva), numFmt(t.comision), numFmt(t.retencion_iva), numFmt(t.admin_fee),
        numFmt(t.liquido), numFmt(t.transferido), numFmt(t.diferencia), '',
      ]));
      var csv = lines.join('\r\n');
      var blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
      var link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'liquidacion-tarjetas-' + this.filtro.mes + '-' + this.filtro.anio + '.csv';
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
  mounted() {
    this.cargar();
  },
};
</script>
